<template>
  <div>
    <!-- Backdrop: declarative @click avoids listener-timing race -->
    <div
      v-if="isVisible && backdropOption && isBackdropVisible"
      class="modal-backdrop"
      :style="backdropStyle"
      data-testid="bs-modal-backdrop"
      @click="handleBackdropClick"
    ></div>

    <div
      v-if="shouldRenderModal"
      :id="modalId"
      ref="modalEl"
      class="modal"
      :class="modalClass"
      :style="modalStyle"
      tabindex="-1"
      :aria-labelledby="`${modalId}-label`"
      :aria-hidden="!isVisible"
      data-testid="bs-modal"
      @click.self="handleBackdropClick"
    >
      <div
        class="modal-dialog"
        :class="dialogClassList"
        :style="dialogStyle"
        role="document"
        data-testid="bs-modal-dialog"
      >
        <div class="modal-content" :class="contentClass">
          <!-- Dim overlay when a child modal is stacked on top -->
          <div v-if="isDimmed" class="modal-dim-overlay"></div>

          <div v-if="!hideHeader" class="modal-header">
            <slot name="modal-header">
              <h5 v-if="title" class="modal-title">{{ title }}</h5>
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                @click="hide('headerclose')"
              ></button>
            </slot>
          </div>

          <div class="modal-body" :class="bodyClass">
            <!--
              Gate slot on hasBeenShown: child components are not instantiated
              before the modal first opens (lazy init), but are preserved across
              hides when destroyOnHide=false.
            -->
            <template v-if="hasBeenShown">
              <slot />
            </template>
          </div>

          <div v-if="!hideFooter" class="modal-footer">
            <slot name="modal-footer">
              <button
                v-if="cancelTitle"
                type="button"
                class="btn btn-secondary"
                @click="hide('cancel')"
              >
                {{ cancelTitle }}
              </button>
              <button
                v-if="okTitle"
                type="button"
                class="btn btn-primary"
                :disabled="okDisabled"
                @click="$emit('ok', $event)"
              >
                {{ okTitle }}
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import ModalPkg from 'bootstrap/js/dist/modal';

const Modal = ModalPkg.default || ModalPkg;

// ---------------------------------------------------------------------------
// Reactive modal stack — shared across all b-modal instances
// ---------------------------------------------------------------------------
const BASE_Z_INDEX = 1050;
const Z_STEP = 2;

const stackState = new Vue({
  data() {
    return { stack: [] };
  },
});

export const modalStack = {
  push(id) {
    if (!stackState.stack.includes(id)) stackState.stack.push(id);
  },
  pop(id) {
    const idx = stackState.stack.indexOf(id);
    if (idx !== -1) stackState.stack.splice(idx, 1);
  },
  isTop(id) {
    return stackState.stack[stackState.stack.length - 1] === id;
  },
  getZIndex(id) {
    const idx = stackState.stack.indexOf(id);
    return BASE_Z_INDEX + (idx === -1 ? 0 : idx) * Z_STEP;
  },
  get stack() {
    return stackState.stack;
  },
  getCount() {
    return stackState.stack.length;
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default {
  name: 'b-modal',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    id: { type: String, default: null },

    /** v-model binding — parent is source of truth */
    value: { type: Boolean, default: undefined },

    size: {
      type: String,
      default: '',
      validator: (v) => !v || ['sm', 'md', 'lg', 'xl', 'huge'].includes(v),
    },

    hideFooter:        { type: Boolean, default: false },
    hideHeader:        { type: Boolean, default: false },
    centered:          { type: Boolean, default: false },
    noCloseOnBackdrop: { type: Boolean, default: false },
    noCloseOnEsc:      { type: Boolean, default: false },

    modalClass:   { type: [String, Array, Object], default: null },
    contentClass: { type: [String, Array, Object], default: null },
    bodyClass:    { type: [String, Array, Object], default: null },

    backdrop:    { type: [Boolean, String], default: true },
    title:       { type: String,  default: null },
    okTitle:     { type: String,  default: 'OK' },
    cancelTitle: { type: String,  default: 'Cancel' },
    okDisabled:  { type: Boolean, default: false },

    /**
     * destroyOnHide=false (default)
     *   Modal stays in DOM after first open → cheap re-opens, slot state preserved.
     * destroyOnHide=true
     *   Modal is fully unmounted after each hide → matches BV lazy/static=false.
     */
    destroyOnHide: { type: Boolean, default: true },
  },

  data() {
    return {
      bsModal: null,
      modalListenersAttached: false,

      // ── render gate ────────────────────────────────────────────────────────
      // isHidden=true  → element not in DOM
      // isHidden=false → element in DOM (visible or kept-alive)
      isHidden: true,

      // ── visibility ─────────────────────────────────────────────────────────
      isVisible: false,     // true while Bootstrap considers modal "shown"

      // ── slot content gate ──────────────────────────────────────────────────
      // Stays true after first open so slot children survive hides
      // when destroyOnHide=false.
      hasBeenShown: false,

      // ── transition-safe state machine ─────────────────────────────────────
      isOpening: false,     // show() in-flight (between call and onShown)
      isClosing: false,     // hide() in-flight (between call and onHidden)

      // Intent queued during a transition; resolved in onHidden.
      _pendingShow: false,
    };
  },

  computed: {
    modalId() {
      return (this.id != null && String(this.id).length > 0)
        ? this.id
        : `bv-modal-${this._uid}`;
    },

    dialogClassList() {
      const c = [];
      if (this.size === 'sm')   c.push('modal-sm');
      if (this.size === 'lg')   c.push('modal-lg');
      if (this.size === 'xl')   c.push('modal-xl');
      if (this.size === 'huge') c.push('modal-huge');
      if (this.centered)        c.push('modal-dialog-centered');
      return c;
    },

    backdropOption() {
      if (this.noCloseOnBackdrop)    return 'static';
      if (this.backdrop === false)    return false;
      if (this.backdrop === 'static') return 'static';
      return true;
    },

    isDimmed() {
      return !modalStack.isTop(this.modalId) && modalStack.getCount() > 1;
    },
    isBackdropVisible() {
      return modalStack.isTop(this.modalId);
    },

    dialogStyle() { return {}; },

    modalStyle() {
      return { zIndex: modalStack.getZIndex(this.modalId) };
    },
    backdropStyle() {
      return { zIndex: modalStack.getZIndex(this.modalId) - 5 };
    },

    // Single, clean render gate.
    shouldRenderModal() {
      return !this.isHidden;
    },
  },

  watch: {
    value(val) {
      if (val === true) {
        this.$nextTick(() => this.show());
      } else if (val === false) {
        this.$nextTick(() => this.hide());
      }
    },
  },

  mounted() {
    if (this.value === true) {
      this.$nextTick(() => this.show());
    }
  },

  beforeDestroy() {
    if (this.isVisible) {
      modalStack.pop(this.modalId);
      this._cleanupBodyStyles();
    }
    this.unbindModal();

    // Only remove from #modal-container if WE teleported there —
    // never remove from Vue's own managed root.
    const container = document.getElementById('modal-container');
    if (container && this.$el && this.$el.parentNode === container) {
      container.removeChild(this.$el);
    }
  },

  methods: {
    // ─────────────────────────────────────────────────────────────────────────
    // Public API
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Open the modal.
     *
     * Handles every race condition:
     *  • Already open              → no-op
     *  • Currently closing         → queues reopen; executes after onHidden
     *  • Currently opening         → no-op (de-dupes double calls)
     *  • Normal / first open       → sets up DOM then calls bsModal.show()
     */
    show() {
      if (this.isVisible && !this.isClosing) return;  // already open

      if (this.isClosing) {
        // Queue reopen; onHidden will flush _pendingShow
        this._pendingShow = true;
        return;
      }

      if (this.isOpening) return;   // de-dupe concurrent calls

      this.isOpening = true;
      this._pendingShow = false;

      // 1. Insert modal element into DOM
      this.isHidden     = false;
      this.hasBeenShown = true;

      // 2. Tick 1: element rendered by Vue → teleport to #modal-container
      this.$nextTick(() => {
        this._moveToModalContainer();

        // Tick 2: element settled in new parent → safe to bootstrap
        this.$nextTick(() => {
          this._ensureInstance();
          if (this.bsModal) {
            this.bsModal.show();
          } else {
            // Bootstrap init failed (shouldn't happen, but be safe)
            this.isOpening = false;
            this.isHidden  = true;
          }
        });
      });
    },

    /**
     * Close the modal.
     * @param {string} trigger  Label forwarded with the 'hide' event.
     */
    hide(trigger = '') {
      if (!this.isVisible || this.isClosing) return;

      this.isClosing = true;
      this.$emit('hide', trigger);

      this._ensureInstance();
      if (this.bsModal) {
        this.bsModal.hide();
      } else {
        // No BS instance — force-close manually
        this.isClosing = false;
        this._forceHidden();
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Bootstrap instance management
    // ─────────────────────────────────────────────────────────────────────────

    _moveToModalContainer() {
      const container = document.getElementById('modal-container');
      if (container && this.$el && this.$el.parentNode !== container) {
        container.appendChild(this.$el);
      }
    },

    bindModal() {
      const el = this.$refs.modalEl;
      if (!el || typeof Modal.getOrCreateInstance !== 'function') return;

      this.bsModal = Modal.getOrCreateInstance(el, {
        backdrop: false,              // We own the backdrop (declarative v-if)
        keyboard: !this.noCloseOnEsc,
      });

      if (!this.modalListenersAttached) {
        el.addEventListener('shown.bs.modal',  this.onShown);
        el.addEventListener('hide.bs.modal',   this._onBsHide);
        el.addEventListener('hidden.bs.modal', this.onHidden);
        this.modalListenersAttached = true;
      }
    },

    _ensureInstance() {
      if (!this.bsModal) this.bindModal();
    },

    unbindModal() {
      const el = this.$refs.modalEl;
      if (el && this.modalListenersAttached) {
        el.removeEventListener('shown.bs.modal',  this.onShown);
        el.removeEventListener('hide.bs.modal',   this._onBsHide);
        el.removeEventListener('hidden.bs.modal', this.onHidden);
        this.modalListenersAttached = false;
      }
      if (this.bsModal) {
        this.bsModal.dispose();
        this.bsModal = null;
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Bootstrap event handlers
    // ─────────────────────────────────────────────────────────────────────────

    onShown() {
      this.isOpening = false;
      if (!this.isVisible) {
        this.isVisible = true;
        modalStack.push(this.modalId);
        this.$emit('input', true);   // keep v-model in sync
        this.$emit('shown');
      }
    },

    // Catches BS-internal hides (e.g. data-bs-dismiss, ESC via BS keyboard option)
    _onBsHide() {
      if (!this.isClosing) {
        this.isClosing = true;
        this.$emit('hide');
      }
    },

    onHidden() {
      this.isClosing = false;

      if (this.isVisible) {
        this._forceHidden();
      }

      // Flush queued show intent
      if (this._pendingShow) {
        this._pendingShow = false;
        this.$nextTick(() => this.show());
      }
    },

    // Shared teardown called by both onHidden and hide()-fallback
    _forceHidden() {
      this.isVisible = false;
      modalStack.pop(this.modalId);
      this._cleanupBodyStyles();
      this.$emit('input', false);   // keep v-model in sync
      this.$emit('hidden');

      if (this.destroyOnHide) {
        this.unbindModal();
        this.hasBeenShown = false;
        this.isHidden = true;       // remove from DOM
      }
      // destroyOnHide=false → leave isHidden=false; element stays alive in DOM
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Backdrop (declarative @click — no listener-timing race possible)
    // ─────────────────────────────────────────────────────────────────────────

    handleBackdropClick() {
      if (!this.noCloseOnBackdrop && this.backdropOption !== 'static') {
        this.hide('backdrop');
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Body scroll-lock helpers
    // ─────────────────────────────────────────────────────────────────────────

    _cleanupBodyStyles() {
      if (modalStack.getCount() === 0) {
        document.body.classList.remove('modal-open');
        document.body.style.overflow    = '';
        document.body.style.paddingRight = '';
      } else {
        // Another modal is still open — keep body locked
        document.body.classList.add('modal-open');
        document.body.style.overflow = 'hidden';
      }
    },
  },
};
</script>

<style>
/* Let the modal scroll internally instead of the page */
.modal {
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

/*
  Custom backdrop — rendered declaratively by Vue, completely replacing
  Bootstrap's own backdrop injection so z-index is fully under our control.
*/
.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  opacity: 1 !important;
  display: block !important;
  visibility: visible !important;
  pointer-events: auto !important;
}

/* Dim overlay on parent modal when a child modal is stacked on top */
.modal-content {
  position: relative;
}

.modal-dim-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  /* Above in-modal chrome (e.g. Profile .prof-header-fixed z-index: 10); still below stacked child modals (1050+ on .modal). */
  z-index: 100;
  border-radius: inherit;
  pointer-events: all;
}
</style>